/**
 * Flywheel mode switcher.
 *
 * The mode switcher changes how the field behaves —
 * not what view you're looking at.
 *
 * Each mode adjusts rendering parameters, layout forces,
 * and overlay visibility through the state store.
 */

import { useAuraStore, type FlyWheelMode } from '../state/store';
import { attachTooltip, hideTooltip } from './tooltip';

// ─── MODE TOOLTIPS ──────────────────────────────────────────

const MODE_TOOLTIPS: Partial<Record<FlyWheelMode, string>> = {
  sense: 'Intuitive exploration — discover the gravitational landscape of intelligence',
  validate: 'Epistemological testing — is this signal real, defensible, and safe to act on?',
  field: 'Live activation — design, open, and evolve a field grounded in cluster intelligence',
};

const MODE_TRANSITION_DURATION = 500; // ms

const MODE_INDEX: Partial<Record<FlyWheelMode, number>> = {
  sense: 0,
  validate: 1,
  field: 2,
};

export class ModeSystem {
  private labels: NodeListOf<Element>;
  private transitionStart = 0;
  private transitioning = false;

  constructor() {
    this.labels = document.querySelectorAll('.mode-label');

    this.labels.forEach((label) => {
      const mode = label.getAttribute('data-mode') as FlyWheelMode;

      // Tooltip for each mode label
      if (mode && MODE_TOOLTIPS[mode]) {
        attachTooltip(label as HTMLElement, MODE_TOOLTIPS[mode]);
      }

      label.addEventListener('click', () => {
        if (mode) this.switchMode(mode);
      });
    });
  }

  private switchMode(mode: FlyWheelMode) {
    const store = useAuraStore.getState();
    if (store.mode === mode) return;

    // Update UI
    this.labels.forEach((l) => l.classList.remove('active'));
    document.querySelector(`[data-mode="${mode}"]`)?.classList.add('active');

    // Begin transition
    hideTooltip();
    store.setMode(mode);
    this.transitionStart = performance.now();
    this.transitioning = true;
  }

  /**
   * Called every frame. Returns the mode index (for shader uniform).
   */
  update(): number {
    const store = useAuraStore.getState();

    if (this.transitioning) {
      const elapsed = performance.now() - this.transitionStart;
      const progress = Math.min(elapsed / MODE_TRANSITION_DURATION, 1);

      // Smooth easing
      const eased = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      store.setModeTransitionProgress(eased);

      if (progress >= 1) {
        this.transitioning = false;
        store.setModeTransitionProgress(1);
      }
    }

    return MODE_INDEX[store.mode] ?? 0;
  }

  dispose() {}
}
